import { Form, Formik } from "formik";
import Select, { ReactSelect } from "../UI/Select";
import {
  DOCUMENT_LIST,
  documentSchema,
  FileStatus,
  isDiagnosticTag,
} from "../../models/documents";
import TextInput from "../UI/TextInput";
import Radio from "../UI/Radio";
import RadioGroup from "../UI/RadioGroup";
import FormUpload from "./FormUpload";
import { sanitizeValues } from "../../models/utils";
import useTenants from "../../hooks/use-tenants";
import { produce } from "immer";
import useFiles from "../../hooks/use-files";
import { useState } from "react";
import Callout from "../UI/Callout";
import { ModalDialogFooter } from "../UI/ModalDialog";
import { handleAPIError } from "../../utils/index.jsx";
import Checkbox from "../UI/Checkbox";
import FormikSubmitButton from "../UI/FormikSubmitButton";
import TenantSelect from "../Tenant/TenantSelect";
import { lazy } from "yup";
import {
  GROUPABLE_DIAGNOSTIC_TAGS,
  DocType,
  META_DIAGNOSTICS,
} from "../../utils/constants/documents.js";
import CheckboxStandalone from "../UI/CheckboxStandalone";
import Text from "../UI/Text";
import styles from "./DocumentsFormUpload.module.css";

function generateYears() {
  const currentYear = new Date().getFullYear();
  const years = [];
  for (let year = currentYear + 1; year >= currentYear - 20; year--) {
    years.push(year);
  }
  return years;
}

/** Diagnostic qui peut être valable sans limite (case « absence constatée »). */
function canBeUnlimited(tag) {
  return !!DOCUMENT_LIST.find((doc) => doc.tag === tag)?.fields.some(
    (field) => field.name === "no_validity",
  );
}

export default function DocumentsFormUpload({
  property,
  propertyFolder,
  tenant,
  profileBailleurId,
  isBailleur,
  isLocataire,
  file,
  onSubmitted,
  typeDocument,
}) {
  const { updateTenant } = useTenants();
  const { updateFile, addFiles } = useFiles({
    tenantId: tenant?.id,
    propertyId: isLocataire ? undefined : property?.id,
    isTenant: isLocataire,
    propertyFolderId: isLocataire ? undefined : propertyFolder?.id,
    profileBailleurId,
  });
  const edit = !!file;
  const [error, setError] = useState("");
  const validationSchema = lazy((values) => {
    const document = DOCUMENT_LIST.find((doc) => doc.tag === values.tag);
    return document?.validationSchema ?? documentSchema;
  });

  async function submitHandler(values, { resetForm }) {
    let sanitizedValues = sanitizeValues(values);
    const newFiles = produce(sanitizedValues.files, (draft) => {
      draft.forEach((file) => {
        file.valid_until = sanitizedValues.no_validity
          ? null
          : sanitizedValues.valid_until;
        file.shared = sanitizedValues.shared;
        file.status = sanitizedValues.status;
        file.tag = sanitizedValues.tag;
        if (sanitizedValues.tag === "quittance") {
          file.meta = {
            date_quittance: sanitizedValues.date_quittance,
          };
        }
        if (
          [
            DocType.LIASSE,
            DocType.TABLEAU_AMORTISSEMENT,
            DocType.TABLEAU_IMMOBILISATION,
          ].includes(sanitizedValues.tag) &&
          sanitizedValues.year
        ) {
          file.meta = {
            year: sanitizedValues.year,
          };
        }
        if (isDiagnosticTag(sanitizedValues.tag)) {
          file.meta = {
            ...(file.meta ?? {}),
            [META_DIAGNOSTICS]: (sanitizedValues.diagnostics ?? []).filter(
              (tag) =>
                tag !== sanitizedValues.tag &&
                (!sanitizedValues.no_validity || canBeUnlimited(tag)),
            ),
          };
        }
        if (sanitizedValues.filename) {
          file.filename = sanitizedValues.filename;
        }
      });
    });
    try {
      if (!isLocataire) {
        // Pour un bail, la date signature fait partie du tenant
        // Update tenant si le form a un field date_signature
        if (sanitizedValues?.date_signature) {
          await updateTenant(tenant.id, {
            date_signature: sanitizedValues.date_signature,
          });
        }
      }

      if (edit) {
        await updateFile(file.id, newFiles[0]);
      } else {
        await addFiles(newFiles);
      }
      onSubmitted();
      resetForm();
      setError("");
    } catch (error) {
      setError(handleAPIError(error));
    }
  }

  // Type présélectionné (ajout rapide d'un document obligatoire) : appliquer
  // les mêmes défauts de cases à cocher que lors d'un changement manuel du
  // select (ex. « Partagé avec le locataire » coché pour les diagnostics).
  const presetDocument =
    !file && typeDocument
      ? DOCUMENT_LIST.find((doc) => doc.tag === typeDocument)
      : null;
  const presetShared = !!presetDocument?.fields.some(
    (field) =>
      field.type === "checkbox" && field.name === "shared" && field.checked,
  );

  const initialValues = {
    id_tenant: file?.id_tenant ?? tenant?.id ?? "",
    id_bien: file?.id_bien ?? property?.id ?? "",
    id_property_folder: file?.id_property_folder ?? propertyFolder?.id ?? "",
    id_profiles_nom: file?.id_profiles_nom ?? profileBailleurId ?? "",
    status: file
      ? file.status
        ? file.status.toString()
        : ""
      : isLocataire
      ? FileStatus.aValider.status.toString()
      : FileStatus.valide.status.toString(),
    files: file ? [file] : [],
    tag: file?.tag || (typeDocument ?? ""),
    valid_until: file?.valid_until || "",
    no_validity: !!file && !file.valid_until && canBeUnlimited(file.tag),
    date_signature: tenant?.date_signature ?? "",
    shared: file?.shared || presetShared,
    filename: file?.filename || "",
    date_quittance: "",
    year: file?.meta?.year || "",
    diagnostics: file?.meta?.[META_DIAGNOSTICS] ?? [],
  };

  if (file?.meta?.date_quittance) {
    initialValues.date_quittance = file?.meta?.date_quittance;
  }

  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true}
      onSubmit={submitHandler}
      validationSchema={validationSchema}
    >
      {({ values, handleChange, setFieldValue }) => {
        const document = DOCUMENT_LIST.find(
          (document) => values.tag === document.tag,
        );
        function changeDocument(e) {
          const document = DOCUMENT_LIST.find(
            (document) => e.target.value === document.tag,
          );
          document.fields.forEach((field) => {
            if (field.type === "checkbox" && field.checked === true) {
              setFieldValue(field.name, true);
            }
          });
          setFieldValue("no_validity", false);
          handleChange(e);
        }

        return (
          <Form>
            <h2 className={"margin0 text-center"}>
              {edit ? "Modifier" : "Ajouter"} un document
            </h2>
            <Select
              name="tag"
              disabled={edit}
              onChange={(e) => changeDocument(e)}
            >
              <option value="">Sélectionnez le type de document</option>
              {!isLocataire &&
                (isBailleur ||
                  property ||
                  propertyFolder ||
                  profileBailleurId) &&
                DOCUMENT_LIST.filter((doc) => {
                  if (property || propertyFolder) return doc.vueProperty;
                  if (profileBailleurId)
                    return (
                      doc.vueBailleur && !doc.addLocataire && !doc.addProperty
                    );
                  return doc.vueBailleur;
                }).map((option, i) => (
                  <option key={i} value={option.tag}>
                    {option.nom}
                  </option>
                ))}

              {isLocataire &&
                DOCUMENT_LIST.filter((doc) => doc.vueLocataire).map(
                  (option, i) => (
                    <option key={i} value={option.tag}>
                      {option.nom}
                    </option>
                  ),
                )}
            </Select>
            {(isBailleur || property || propertyFolder) && (
              <TextInput
                type="text"
                name="filename"
                label="Nom du document"
                placeholder="règlement de copro"
              />
            )}
            {document?.fields
              .filter((field) => {
                if (field.name === "valid_until" && values.no_validity)
                  return false;
                if (property || propertyFolder)
                  return field.vueProperty ?? true;
                if (tenant) return field.vueTenant ?? true;
                return true;
              })
              .map((field, i) =>
                field.type === "checkbox" ? (
                  <Checkbox key={i} name={field.name}>
                    {field.label}
                  </Checkbox>
                ) : field.type === "diagnostics" ? (
                  <div key={i} className={styles.diagnosticsGroup}>
                    <Text weight="medium">{field.label}</Text>
                    <div className={styles.diagnosticsOptions}>
                      {GROUPABLE_DIAGNOSTIC_TAGS.filter(
                        (tag) =>
                          tag !== values.tag &&
                          (!values.no_validity || canBeUnlimited(tag)),
                      ).map((tag) => (
                        <CheckboxStandalone
                          key={tag}
                          checked={values.diagnostics.includes(tag)}
                          onChange={(e) =>
                            setFieldValue(
                              "diagnostics",
                              e.target.checked
                                ? [...values.diagnostics, tag]
                                : values.diagnostics.filter((t) => t !== tag),
                            )
                          }
                        >
                          {DOCUMENT_LIST.find((doc) => doc.tag === tag).nom}
                        </CheckboxStandalone>
                      ))}
                    </div>
                  </div>
                ) : field.type === "year" ? (
                  <ReactSelect
                    key={i}
                    name={field.name}
                    label={field.label}
                    placeholder="Sélectionnez une année"
                    options={generateYears().map((year) => ({
                      label: year.toString(),
                      value: year.toString(),
                    }))}
                  />
                ) : (
                  <TextInput
                    type={field.type}
                    name={field.name}
                    placeholder={field.label}
                    label={field.label}
                    subText={
                      field.name === "valid_until"
                        ? document.validityHint
                        : undefined
                    }
                    key={i}
                  />
                ),
              )}
            {(!property || !propertyFolder) && (
              <>
                {document?.addLocataire && !initialValues.id_tenant && (
                  <TenantSelect name="id_tenant" withArchivedTenants />
                )}
                {document?.needValidation && isBailleur && (
                  <RadioGroup className={"marginT10"}>
                    <Radio
                      name="status"
                      value={FileStatus.valide.status.toString()}
                      label="Validé"
                    />
                    <Radio
                      name="status"
                      value={FileStatus.aValider.status.toString()}
                      label="A valider"
                    />
                  </RadioGroup>
                )}
              </>
            )}
            <FormUpload
              tag={document?.tag}
              valid_until={values.valid_until}
              status={values.status}
              edit={edit}
              noTagFilter
            />

            <Callout variant="danger">{error}</Callout>
            <ModalDialogFooter>
              <FormikSubmitButton requiresWrite profileId={profileBailleurId}>
                Enregistrer
              </FormikSubmitButton>
            </ModalDialogFooter>
          </Form>
        );
      }}
    </Formik>
  );
}
